// Parse and patch the \quibly* macros in compiled LaTeX source so the
// structured editor can present editable text fields without AI.
//
// The composer produces a predictable structure:
//   %----------EXPERIENCE----------
//   \section{...}
//   \quiblyEntryStart
//     \quiblyEntry{title}{dates}{subtitle}{detail}
//       \quiblyBulletsStart
//         \quiblyItem{bullet text}
//       \quiblyBulletsEnd
//   \quiblyEntryEnd
//
// We parse each section into an array of entries (with nested bullets) and
// provide a `patchLatex` function that writes changed values back.

const SECTION_MARKERS = [
  { key: 'summary', marker: 'SUMMARY' },
  { key: 'experience', marker: 'EXPERIENCE' },
  { key: 'projects', marker: 'PROJECTS' },
  { key: 'education', marker: 'EDUCATION' },
  { key: 'skills', marker: 'SKILLS' },
  { key: 'certs', marker: 'CERTS' },
  { key: 'publications', marker: 'PUBLICATIONS' }
];

const unescapeLatex = (text) =>
  text
    .replace(/\\textbackslash\{\}/g, '\\')
    .replace(/\\&/g, '&')
    .replace(/\\%/g, '%')
    .replace(/\\\$/g, '$')
    .replace(/\\#/g, '#')
    .replace(/\\_/g, '_')
    .replace(/\\\{/g, '{')
    .replace(/\\\}/g, '}')
    .replace(/\\textasciitilde\{\}/g, '~')
    .replace(/\\textasciicircum\{\}/g, '^');

const escapeLatex = (value) => {
  if (value == null) return '';
  return String(value)
    .replace(/\\/g, '\\textbackslash{}')
    .replace(/&/g, '\\&')
    .replace(/%/g, '\\%')
    .replace(/\$/g, '\\$')
    .replace(/#/g, '\\#')
    .replace(/_/g, '\\_')
    .replace(/\{/g, '\\{')
    .replace(/\}/g, '\\}')
    .replace(/~/g, '\\textasciitilde{}')
    .replace(/\^/g, '\\textasciicircum{}');
};

// Extract the content inside balanced braces starting at `start`.
const extractBraced = (text, start) => {
  if (start >= text.length || text[start] !== '{') return null;
  let depth = 0;
  for (let i = start; i < text.length; i++) {
    if (text[i] === '{') depth++;
    else if (text[i] === '}') {
      depth--;
      if (depth === 0) return { content: text.slice(start + 1, i), end: i + 1 };
    }
  }
  return null;
};

// Extract the slice of LaTeX that belongs to `marker` (e.g. "EXPERIENCE").
const getSectionSlice = (latex, marker) => {
  const tag = `%----------${marker}----------`;
  const start = latex.indexOf(tag);
  if (start === -1) return null;
  const nextTag = latex.indexOf('%----------', start + tag.length);
  const endIdx = nextTag === -1 ? latex.indexOf('\\end{document}', start) : nextTag;
  if (endIdx === -1) return null;
  return { start, end: endIdx, text: latex.slice(start, endIdx) };
};

// Parse all \quiblyEntry{a}{b}{c}{d} occurrences and their \quiblyItem bullets
// from a section slice.
const parseEntries = (sectionText) => {
  const entries = [];
  const entryRe = /\\quiblyEntry\s*\{/g;
  let match;
  while ((match = entryRe.exec(sectionText)) !== null) {
    const fields = [];
    let pos = match.index + match[0].length - 1; // at the opening brace
    for (let f = 0; f < 4; f++) {
      const braced = extractBraced(sectionText, pos);
      if (!braced) break;
      fields.push(braced.content);
      pos = braced.end;
    }
    if (fields.length < 4) continue;

    // Find bullets between this entry and the next (or end of section)
    const entryEnd = pos;
    const nextEntry = sectionText.indexOf('\\quiblyEntry', entryEnd);
    const bulletZone = nextEntry === -1
      ? sectionText.slice(entryEnd)
      : sectionText.slice(entryEnd, nextEntry);

    const bullets = [];
    const bulletRe = /\\quiblyItem\s*\{/g;
    let bm;
    while ((bm = bulletRe.exec(bulletZone)) !== null) {
      const braced = extractBraced(bulletZone, bm.index + bm[0].length - 1);
      if (braced) bullets.push(braced.content);
    }

    entries.push({
      title: unescapeLatex(fields[0]),
      dates: unescapeLatex(fields[1]),
      subtitle: unescapeLatex(fields[2]),
      detail: unescapeLatex(fields[3]),
      bullets: bullets.map(unescapeLatex),
      _raw: sectionText.slice(match.index, entryEnd),
      _rawWithBullets: sectionText.slice(match.index, nextEntry === -1 ? sectionText.length : nextEntry)
    });
  }
  return entries;
};

// Parse the summary text from \small ... inside the SUMMARY section.
const parseSummary = (sectionText) => {
  const match = sectionText.match(/\\small\s+([\s\S]*?)(?=\n\n|%---|\\end\{|$)/);
  if (!match) return '';
  return unescapeLatex(match[1].trim());
};

// Parse the heading contact fields from the HEADING section.
const parseHeading = (latex) => {
  const slice = getSectionSlice(latex, 'HEADING');
  if (!slice) return null;
  const text = slice.text;

  const nameMatch = text.match(/\\textbf\{([^}]*)\}/);
  const fullName = nameMatch ? unescapeLatex(nameMatch[1]) : '';

  const headlineMatch = text.match(/\\large\s+([^}\\]+(?:\\[^{]*\{[^}]*\})*)/);
  let headline = '';
  if (headlineMatch) {
    headline = unescapeLatex(headlineMatch[1].replace(/\}?\s*$/, '').trim());
  }

  return { fullName, headline };
};

// Parse skills from the SKILLS section. The composer can emit four formats:
//   1. tabular*:   \textbf{Skills} & skill1, skill2\\
//   2. center:     \small skill1 \,\textbullet\, skill2
//   3. itemize:    \textbf{Languages}{: skill1, skill2}
//   4. plain:      \textbf{Skills}: skill1, skill2
// We strip structural LaTeX and extract the comma / textbullet-separated values.
const parseSkills = (sectionText) => {
  // Remove the section header lines (marker + \section{...} + decoration).
  let body = sectionText
    .replace(/%----------[A-Z]+----------/g, '')
    .replace(/\\section\*?\{[^}]*\}/g, '')
    .replace(/\\titlerule(\s*\[[^\]]*\])?\s*/g, '')
    .replace(/\\hrule\s*/g, '')
    .replace(/\\vspace\*?\{[^}]*\}\s*/g, '');

  // Strip structural commands but keep the text values.
  body = body
    .replace(/\\begin\{[^}]*\}(\[[^\]]*\])?\s*/g, '')
    .replace(/\\end\{[^}]*\}\s*/g, '')
    .replace(/\\small\s*/g, '')
    .replace(/\\item\s*/g, '')
    .replace(/\\textbf\{[^}]*\}\{?:?\s*/g, '')
    .replace(/\\textbullet/g, ',')
    .replace(/\\,/g, '')
    .replace(/\\extracolsep\{[^}]*\}/g, '')
    .replace(/\\hfill\s*/g, '')
    .replace(/\\\\(\[[^\]]*\])?\s*/g, ',')
    .replace(/&/g, ',')
    .replace(/[{}]/g, '')
    .replace(/\s*l@\s*/g, '')
    .replace(/\s*\\textwidth\s*/g, '')
    .replace(/\[t\]/g, '');

  return body
    .split(',')
    .map((s) => unescapeLatex(s.trim()))
    .filter((s) => s.length > 0 && !/^(leftmargin|label)\s*=/i.test(s));
};

// Main parse: returns a structured representation of all editable sections.
export const parseLatexFields = (latex) => {
  const sections = {};
  for (const { key, marker } of SECTION_MARKERS) {
    const slice = getSectionSlice(latex, marker);
    if (!slice) continue;
    if (key === 'summary') {
      sections.summary = { text: parseSummary(slice.text), _slice: slice };
    } else if (key === 'skills') {
      sections.skills = { items: parseSkills(slice.text), _slice: slice };
    } else {
      sections[key] = { entries: parseEntries(slice.text), _slice: slice };
    }
  }
  sections.heading = parseHeading(latex);
  return sections;
};

// Rebuild a single entry's LaTeX from edited field values.
const buildEntryLatex = (entry) => {
  const lines = [
    `  \\quiblyEntry{${escapeLatex(entry.title)}}{${escapeLatex(entry.dates)}}{${escapeLatex(entry.subtitle)}}{${escapeLatex(entry.detail)}}`
  ];
  if (entry.bullets && entry.bullets.length > 0) {
    lines.push('  \\quiblyBulletsStart');
    for (const b of entry.bullets) {
      if (b.trim()) lines.push(`    \\quiblyItem{${escapeLatex(b.trim())}}`);
    }
    lines.push('  \\quiblyBulletsEnd');
  }
  return lines.join('\n');
};

// Build the full section body from an array of entries.
const buildSectionBody = (entries) => {
  if (!entries.length) return '';
  return [
    '\\quiblyEntryStart',
    ...entries.map(buildEntryLatex),
    '\\quiblyEntryEnd'
  ].join('\n');
};

// Replace the body of a section (between the \section{...} line and the next
// marker or \end{document}) with new content.
const replaceSectionBody = (latex, marker, newBody) => {
  const slice = getSectionSlice(latex, marker);
  if (!slice) return latex;

  const sectionText = slice.text;
  const sectionCmd = sectionText.match(/\\section\*?\{/);
  if (!sectionCmd) return latex;
  const braced = extractBraced(sectionText, sectionText.indexOf('{', sectionCmd.index));
  if (!braced) return latex;

  const afterTitle = sectionText.slice(braced.end);
  const decoMatch = afterTitle.match(/^(\s*(?:\\(?:titlerule|hrule|vspace|rule|sectionrule|smallskip|medskip|bigskip|noindent|par|color)\b[^\n]*\n?)*)/);
  const decoration = decoMatch ? decoMatch[1] : '';
  const headerEnd = braced.end + decoration.length;

  const header = sectionText.slice(0, headerEnd).replace(/^%----------[A-Z]+----------\n?/, '');
  return latex.slice(0, slice.start) + `%----------${marker}----------\n${header}\n${newBody}\n` + latex.slice(slice.end);
};

// Patch the full LaTeX source with updated field values. `fields` has the same
// shape as `parseLatexFields` output but with user-edited values.
export const patchLatex = (latex, fields) => {
  let result = latex;

  if (fields.heading) {
    const headingSlice = getSectionSlice(result, 'HEADING');
    if (headingSlice) {
      let headingText = headingSlice.text;
      if (fields.heading.fullName !== undefined) {
        headingText = headingText.replace(
          /\\textbf\{[^}]*\}/,
          `\\textbf{${escapeLatex(fields.heading.fullName)}}`
        );
      }
      result = result.slice(0, headingSlice.start) + headingText + result.slice(headingSlice.end);
    }
  }

  if (fields.summary && fields.summary.text !== undefined) {
    const slice = getSectionSlice(result, 'SUMMARY');
    if (slice) {
      const newText = slice.text.replace(
        /\\small\s+[\s\S]*?(?=\n\n|%---|\\end\{|$)/,
        `\\small ${escapeLatex(fields.summary.text)}`
      );
      result = result.slice(0, slice.start) + newText + result.slice(slice.end);
    }
  }

  for (const key of ['experience', 'projects', 'education', 'certs', 'publications']) {
    if (!fields[key]?.entries) continue;
    const marker = SECTION_MARKERS.find((m) => m.key === key)?.marker;
    if (!marker) continue;
    result = replaceSectionBody(result, marker, buildSectionBody(fields[key].entries));
  }

  if (fields.skills?.items) {
    const slice = getSectionSlice(result, 'SKILLS');
    if (slice) {
      // Rebuild skills in a simple format matching what the composer uses.
      const list = fields.skills.items.filter(Boolean);
      const half = Math.ceil(list.length / 2);
      const left = list.slice(0, half).map(escapeLatex).join(', ');
      const right = list.slice(half).map(escapeLatex).join(', ');
      const sectionText = slice.text;
      const sectionCmd = sectionText.match(/\\section\*?\{/);
      if (sectionCmd) {
        const braced = extractBraced(sectionText, sectionText.indexOf('{', sectionCmd.index));
        if (braced) {
          const header = sectionText.slice(0, braced.end);
          const skillsBody = [
            '\\small',
            `\\textbf{Skills}: ${left}\\\\[2pt]`,
            right ? `\\textbf{Technologies}: ${right}` : ''
          ].filter(Boolean).join('\n');
          const newSection = `%----------SKILLS----------\n${header.replace(/^%----------SKILLS----------\n?/, '')}\n${skillsBody}\n`;
          result = result.slice(0, slice.start) + newSection + result.slice(slice.end);
        }
      }
    }
  }

  return result;
};

// Build a new section LaTeX block that can be injected into the document for a
// profile item that wasn't originally selected by the AI. This avoids needing
// an AI call — we use the same deterministic composer logic.
export const buildNewSectionBlock = (sectionKey, entries) => {
  const marker = SECTION_MARKERS.find((m) => m.key === sectionKey)?.marker;
  if (!marker) return '';
  const titleMap = {
    experience: 'Experience',
    projects: 'Projects',
    education: 'Education',
    certs: 'Certifications',
    publications: 'Publications'
  };
  const title = titleMap[sectionKey] || sectionKey;
  return `%----------${marker}----------\n\\section{${title}}\n${buildSectionBody(entries)}\n`;
};

// Insert a new entry into an existing section in the LaTeX source.
export const insertEntryIntoSection = (latex, sectionKey, entry) => {
  const marker = SECTION_MARKERS.find((m) => m.key === sectionKey)?.marker;
  if (!marker) return latex;
  const slice = getSectionSlice(latex, marker);
  if (!slice) {
    // Section doesn't exist yet — add before \end{document}
    const endDoc = latex.lastIndexOf('\\end{document}');
    if (endDoc === -1) return latex;
    const block = buildNewSectionBlock(sectionKey, [entry]);
    return latex.slice(0, endDoc) + '\n' + block + '\n' + latex.slice(endDoc);
  }

  // Insert before \quiblyEntryEnd
  const entryEndPos = slice.text.lastIndexOf('\\quiblyEntryEnd');
  if (entryEndPos === -1) return latex;
  const insertPos = slice.start + entryEndPos;
  const entryLatex = buildEntryLatex(entry) + '\n';
  return latex.slice(0, insertPos) + entryLatex + latex.slice(insertPos);
};

// Remove an entry from a section by its index (0-based among entries in that section).
export const removeEntryFromSection = (latex, sectionKey, entryIndex) => {
  const marker = SECTION_MARKERS.find((m) => m.key === sectionKey)?.marker;
  if (!marker) return latex;
  const slice = getSectionSlice(latex, marker);
  if (!slice) return latex;

  const entries = parseEntries(slice.text);
  if (entryIndex < 0 || entryIndex >= entries.length) return latex;

  const remaining = entries.filter((_, i) => i !== entryIndex);
  if (remaining.length === 0) {
    // Remove the entire section
    return latex.slice(0, slice.start) + latex.slice(slice.end);
  }
  return replaceSectionBody(latex, marker, buildSectionBody(remaining));
};
